/**
 * Recipe Ingredients Section
 * Component for managing ingredients linked to a recipe
 * Features:
 * - Inline search dropdown at top for adding ingredients
 * - Server-side search with debouncing for scalability
 * - Quick-create for new ingredients when search returns no results
 * - Real-time updates: Add/Remove instant, quantity saves on commit, notes debounced
 * - All operations require a recipeId (used after recipe is created)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { useUIStore } from '@/stores/uiStore';
import { useRecipeIngredientsCacheStore } from '@/stores/recipeIngredientsCacheStore';
import {
  fetchRecipeIngredients,
  addRecipeIngredient,
  updateRecipeIngredient,
  removeRecipeIngredient,
} from '@/lib/api/recipeIngredients';
import { fetchIngredients, createIngredient, fetchUnitSuggestion } from '@/lib/api/ingredients';
import { describeSuggestion } from '@/components/admin/library/IngredientLibrarySection';
import type {
  RecipeIngredient,
  Ingredient,
  IngredientCategory,
  CreateIngredientRequest,
} from '@/types/api';
import {
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
} from '@/types/api';
import {
  formatScaledQuantity,
  RECIPE_QUANTITY_PER_SERVING_MAX,
  RECIPE_QUANTITY_PER_SERVING_MAX_MESSAGE,
  applyUnitSuggestion,
  getDefaultUnitForCategory,
  type UnitSource,
} from '@/types/units';
import { UnitGroupSelectContent } from '@/components/admin/UnitGroupSelectContent';
import { QuantitySaveIndicator, type QuantitySaveState } from '@/components/admin/QuantitySaveIndicator';

// Constants
const DROPDOWN_MAX_RESULTS = 10;
const DEFAULT_INGREDIENT_CATEGORY: IngredientCategory = 'OTHER';
const SEARCH_DEBOUNCE_MS = 300;
const UPDATE_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;
const QUANTITY_PER_LARGE_DRAFT_PATTERN = /^\d*\.?\d*$/;
const QUANTITY_SAVE_SUCCESS_MS = 1800;
// Whole numbers without leading zeros, so the draft always matches the parsed preview count
const THAALI_MULTIPLIER_DRAFT_PATTERN = /^$|^[1-9]\d*$/;
const THAALI_MULTIPLIER_MAX_LENGTH = 4;

function isQuantityPerLargeDraft(value: string): boolean {
  return QUANTITY_PER_LARGE_DRAFT_PATTERN.test(value);
}

/** Parse the scaled-preview thaali count; whole numbers > 0 only, else null (no preview). */
function parseThaaliMultiplier(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
}

type ParsedQuantity =
  | { ok: true; value: number | null }
  | { ok: false; message: string; reason: 'nonPositive' }
  | { ok: false; message: string; reason: 'tooLarge'; value: number };

function parseQuantityPerLarge(value: string): ParsedQuantity {
  const trimmed = value.trim();
  if (!trimmed) return { ok: true, value: null };

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { ok: false, message: 'Must be greater than 0', reason: 'nonPositive' };
  }
  if (parsed > RECIPE_QUANTITY_PER_SERVING_MAX) {
    return { ok: false, message: RECIPE_QUANTITY_PER_SERVING_MAX_MESSAGE, reason: 'tooLarge', value: parsed };
  }

  return { ok: true, value: parsed };
}

// Local ingredient state for optimistic updates
interface LocalIngredient {
  id: number;
  recipeId: number;
  ingredientId: number;
  quantityPerLarge: string;
  unit: string;
  notes: string;
  ingredient?: Ingredient;
  // Track if this is being added (optimistic update pending)
  isPending?: boolean;
}

interface RecipeIngredientsSectionProps {
  recipeId: number; // Required - component only used after recipe exists
  onCountChange?: (count: number) => void;
}

export function RecipeIngredientsSection({ recipeId, onCountChange }: RecipeIngredientsSectionProps) {
  const { showToast } = useUIStore();

  // Recipe ingredients cache for instant loading
  const getCachedIngredients = useRecipeIngredientsCacheStore((s) => s.get);
  const setCachedIngredients = useRecipeIngredientsCacheStore((s) => s.set);
  const invalidateCache = useRecipeIngredientsCacheStore((s) => s.invalidate);

  // State
  const [ingredients, setIngredients] = useState<LocalIngredient[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [latestSearchQuery, setLatestSearchQuery] = useState('');
  const [quantitySaveStates, setQuantitySaveStates] = useState<Record<number, QuantitySaveState>>({});
  const [thaaliMultiplierDraft, setThaaliMultiplierDraft] = useState('');
  const thaaliMultiplier = parseThaaliMultiplier(thaaliMultiplierDraft);

  // Quick-create ingredient state
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickCreateName, setQuickCreateName] = useState('');
  const [quickCreateCategory, setQuickCreateCategory] = useState<IngredientCategory>(DEFAULT_INGREDIENT_CATEGORY);
  const [quickCreateUnit, setQuickCreateUnit] = useState(getDefaultUnitForCategory(DEFAULT_INGREDIENT_CATEGORY) ?? '');
  const [quickUnitTouched, setQuickUnitTouched] = useState(false);
  // Mirrored in a ref so an in-flight suggestion reads the flag at the moment it resolves.
  const quickUnitTouchedRef = useRef(false);
  const quickUnitSourceRef = useRef<UnitSource>('category');
  const quickCategoryRef = useRef<IngredientCategory>(DEFAULT_INGREDIENT_CATEGORY);
  const markQuickUnitTouched = (touched: boolean) => {
    quickUnitTouchedRef.current = touched;
    setQuickUnitTouched(touched);
    quickUnitSourceRef.current = touched ? 'user' : 'category';
  };
  const [quickUnitNote, setQuickUnitNote] = useState<string | null>(null);
  const quickSuggestionSeq = useRef(0);
  const latestQuickNameRef = useRef('');
  latestQuickNameRef.current = quickCreateName.trim();
  quickCategoryRef.current = quickCreateCategory;

  // Unit suggestion for the quick-create name (history of a same-named ingredient, or the herb rule).
  // Every change bumps the sequence so an earlier request can no longer write into the form.
  useEffect(() => {
    const seq = ++quickSuggestionSeq.current;
    if (!showQuickCreate) return;
    const name = quickCreateName.trim();
    if (name.length < 2) {
      setQuickUnitNote(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await fetchUnitSuggestion({ name });
        if (seq !== quickSuggestionSeq.current || latestQuickNameRef.current !== name) return;
        const { unit } = describeSuggestion(result, { applied: false });
        const next = applyUnitSuggestion({ suggestedUnit: unit, source: quickUnitSourceRef.current, category: quickCategoryRef.current });
        if (next) {
          quickUnitSourceRef.current = next.source;
          setQuickCreateUnit(next.unit);
        }
        setQuickUnitNote(describeSuggestion(result, { applied: next?.source === 'suggestion' }).note);
      } catch {
        /* suggestion only */
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [quickCreateName, showQuickCreate]);
  const [isCreating, setIsCreating] = useState(false);

  // Track debounce timeouts for real-time updates (ingredientId → timeoutId)
  const updateDebounceRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const quantitySaveSuccessTimeoutRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const savedQuantityValuesRef = useRef<Map<number, string>>(new Map());
  const latestLoadRequestIdRef = useRef(0);

  // Track latest ingredients state for use in debounced callbacks (avoid stale closures)
  const ingredientsRef = useRef<LocalIngredient[]>([]);
  ingredientsRef.current = ingredients;

  // Track latest recipeId for use in debounced callbacks (avoid stale closures)
  const recipeIdRef = useRef(recipeId);
  recipeIdRef.current = recipeId;

  // Cleanup debounce timeouts when recipeId changes or on unmount
  useEffect(() => {
    return () => {
      updateDebounceRef.current.forEach((timeout) => clearTimeout(timeout));
      updateDebounceRef.current.clear();
      quantitySaveSuccessTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      quantitySaveSuccessTimeoutRef.current.clear();
    };
  }, [recipeId]);

  function clearQuantitySuccessTimeout(ingredientId: number) {
    const existingTimeout = quantitySaveSuccessTimeoutRef.current.get(ingredientId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      quantitySaveSuccessTimeoutRef.current.delete(ingredientId);
    }
  }

  function setQuantitySaveState(ingredientId: number, state: QuantitySaveState) {
    setQuantitySaveStates((prev) => ({ ...prev, [ingredientId]: state }));
  }

  function syncSavedQuantityValues(nextIngredients: LocalIngredient[]) {
    savedQuantityValuesRef.current = new Map(
      nextIngredients.map((ingredient) => [ingredient.ingredientId, ingredient.quantityPerLarge])
    );
  }

  function setSavedQuantityValue(ingredient: LocalIngredient) {
    savedQuantityValuesRef.current.set(ingredient.ingredientId, ingredient.quantityPerLarge);
  }

  // Load recipe ingredients function
  const loadIngredients = useCallback(async () => {
    const requestId = ++latestLoadRequestIdRef.current;
    setLoadError(null);
    try {
      let nextIngredients: LocalIngredient[];
      // Check cache first, then fetch if needed
      const cached = getCachedIngredients(recipeId);
      if (cached) {
        nextIngredients = cached.map(toLocalIngredient);
      } else {
        const recipeIngs = await fetchRecipeIngredients(recipeId);
        setCachedIngredients(recipeId, recipeIngs);
        nextIngredients = recipeIngs.map(toLocalIngredient);
      }
      if (requestId !== latestLoadRequestIdRef.current) return;
      syncSavedQuantityValues(nextIngredients);
      setQuantitySaveStates({});
      setIngredients(nextIngredients);
      onCountChange?.(getConfirmedIngredientCount(nextIngredients));
    } catch {
      if (requestId !== latestLoadRequestIdRef.current) return;
      setIngredients([]);
      setLoadError('Failed to load ingredients');
      showToast('Failed to load ingredients', 'error');
    }
  }, [recipeId, getCachedIngredients, onCountChange, setCachedIngredients, showToast]);

  // Load recipe ingredients on mount or recipeId change
  useEffect(() => {
    void loadIngredients();
  }, [loadIngredients]);

  function toLocalIngredient(ri: RecipeIngredient): LocalIngredient {
    return {
      id: ri.id,
      recipeId: ri.recipeId,
      ingredientId: ri.ingredientId,
      quantityPerLarge: ri.quantityPerLarge || '',
      unit: ri.unit,
      notes: ri.notes || '',
      ingredient: ri.ingredient,
      isPending: false,
    };
  }

  const searchIngredients = useCallback(async (query: string) => {
    const response = await fetchIngredients({
      search: query,
      pageSize: DROPDOWN_MAX_RESULTS,
      activeOnly: true,
    });
    const activeIngredientIds = new Set(
      ingredientsRef.current.map((i) => i.ingredientId)
    );
    return response.ingredients.filter((ing) => !activeIngredientIds.has(ing.id));
  }, []);

  // Add ingredient - instant API call
  async function handleAddIngredient(ingredient: Ingredient): Promise<boolean> {
    // Generate temporary negative ID for optimistic update tracking
    const tempId = -Date.now();
    const newLocal: LocalIngredient = {
      id: tempId,
      recipeId: recipeId,
      ingredientId: ingredient.id,
      quantityPerLarge: '',
      unit: ingredient.defaultUnit,
      notes: '',
      ingredient,
      isPending: true,
    };

    // Update UI immediately (optimistic update)
    setIngredients((prev) => [...prev, newLocal]);
    setShowQuickCreate(false);

    // Call API
    try {
      const savedIngredient = await addRecipeIngredient(recipeId, {
        ingredientId: ingredient.id,
        quantityPerLarge: null,
        notes: null,
      });

      // Update local state with server response - match by tempId to avoid race conditions
      const savedLocalIngredient = toLocalIngredient(savedIngredient);
      setSavedQuantityValue(savedLocalIngredient);
      let nextIngredients: LocalIngredient[] = [];
      setIngredients((prev) =>
        {
          nextIngredients = prev.map((i) =>
            i.id === tempId
              ? savedLocalIngredient
              : i
          );
          return nextIngredients;
        }
      );

      // Invalidate cache
      invalidateCache(recipeId);
      onCountChange?.(getConfirmedIngredientCount(nextIngredients));
      return true;
    } catch (error) {
      // Remove from UI on error - match by tempId
      setIngredients((prev) => prev.filter((i) => i.id !== tempId));
      showToast(
        error instanceof Error ? error.message : 'Failed to add ingredient',
        'error'
      );
      return false;
    }
  }

  // Handle quick-create ingredient
  async function handleQuickCreate() {
    if (!quickCreateName.trim() || !quickCreateUnit) {
      showToast('Name and unit are required', 'error');
      return;
    }

    setIsCreating(true);
    try {
      const newIngredientData: CreateIngredientRequest = {
        name: quickCreateName.trim(),
        category: quickCreateCategory,
        defaultUnit: quickCreateUnit.trim(),
      };

      const newIngredient = await createIngredient(newIngredientData);

      // Add to recipe and wait for result
      const addSuccess = await handleAddIngredient(newIngredient);

      // Only show success if both create and add succeeded
      if (addSuccess) {
        showToast(`Created "${newIngredient.name}" and added to recipe`, 'success');
      }

      resetQuickCreateForm();
      setShowQuickCreate(false);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to create ingredient',
        'error'
      );
    } finally {
      setIsCreating(false);
    }
  }

  // Clear everything the previous quick-create left behind (category, unit, the
  // "picked by hand" flag and any in-flight suggestion) so a form reopened for a
  // different name starts from its own category default and suggestion.
  function resetQuickCreateForm() {
    quickSuggestionSeq.current += 1;
    setQuickCreateName('');
    setQuickCreateCategory(DEFAULT_INGREDIENT_CATEGORY);
    setQuickCreateUnit(getDefaultUnitForCategory(DEFAULT_INGREDIENT_CATEGORY) ?? '');
    markQuickUnitTouched(false);
    setQuickUnitNote(null);
  }

  // Start quick-create with pre-filled name from search
  function startQuickCreate() {
    resetQuickCreateForm();
    setQuickCreateName(latestSearchQuery);
    setShowQuickCreate(true);
  }

  function handleQuantityChange(ingredientId: number, value: string) {
    if (!isQuantityPerLargeDraft(value)) return;

    clearQuantitySuccessTimeout(ingredientId);
    setQuantitySaveState(ingredientId, { status: 'idle' });
    setIngredients((prev) =>
      prev.map((i) => {
        if (i.ingredientId !== ingredientId) return i;
        return { ...i, quantityPerLarge: value };
      })
    );
  }

  async function commitQuantity(ingredientId: number, value: string) {
    const ingredient = ingredientsRef.current.find((i) => i.ingredientId === ingredientId);
    if (!ingredient || ingredient.isPending) return;

    if (value !== ingredient.quantityPerLarge) {
      setIngredients((prev) =>
        prev.map((i) => {
          if (i.ingredientId !== ingredientId) return i;
          return { ...i, quantityPerLarge: value };
        })
      );
    }

    const savedValue = savedQuantityValuesRef.current.get(ingredientId) ?? '';
    if (value === savedValue) return;

    const parsed = parseQuantityPerLarge(value);
    if (!parsed.ok) {
      clearQuantitySuccessTimeout(ingredientId);
      setQuantitySaveState(ingredientId, { status: 'error', message: parsed.message });
      return;
    }

    clearQuantitySuccessTimeout(ingredientId);
    setQuantitySaveState(ingredientId, { status: 'saving' });

    try {
      await updateRecipeIngredient(recipeIdRef.current, ingredientId, {
        quantityPerLarge: parsed.value,
      });
      savedQuantityValuesRef.current.set(ingredientId, value);
      invalidateCache(recipeIdRef.current);
      const latestIngredient = ingredientsRef.current.find((i) => i.ingredientId === ingredientId);
      if (latestIngredient?.quantityPerLarge !== value) return;
      setQuantitySaveState(ingredientId, { status: 'saved' });
      const timeoutId = setTimeout(() => {
        setQuantitySaveStates((prev) => {
          if (prev[ingredientId]?.status !== 'saved') return prev;
          return { ...prev, [ingredientId]: { status: 'idle' } };
        });
        quantitySaveSuccessTimeoutRef.current.delete(ingredientId);
      }, QUANTITY_SAVE_SUCCESS_MS);
      quantitySaveSuccessTimeoutRef.current.set(ingredientId, timeoutId);
    } catch (error) {
      const latestIngredient = ingredientsRef.current.find((i) => i.ingredientId === ingredientId);
      if (latestIngredient?.quantityPerLarge !== value) return;
      setQuantitySaveState(ingredientId, {
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to save',
      });
    }
  }

  // Update notes - debounced API call
  function handleUpdateNotes(ingredientId: number, value: string) {
    // Capture the previous value before updating
    const previousIngredient = ingredients.find((i) => i.ingredientId === ingredientId);
    const previousValue = previousIngredient ? previousIngredient.notes : '';

    // Update local state immediately for responsive UI
    setIngredients((prev) =>
      prev.map((i) => {
        if (i.ingredientId !== ingredientId) return i;
        return { ...i, notes: value };
      })
    );

    // Clear existing timeout for this ingredient
    const existingTimeout = updateDebounceRef.current.get(ingredientId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new debounced update
    const timeoutId = setTimeout(async () => {
      updateDebounceRef.current.delete(ingredientId);

      // Get the latest ingredient state from ref (avoid stale closure)
      const ingredient = ingredientsRef.current.find((i) => i.ingredientId === ingredientId);
      if (!ingredient || ingredient.isPending) return;

      try {
        const updates: Partial<{
          quantityPerLarge: number | null;
          notes: string | null;
        }> = {};

        updates.notes = value || null;

        await updateRecipeIngredient(recipeIdRef.current, ingredientId, updates);

        // Invalidate cache
        invalidateCache(recipeIdRef.current);
      } catch (error) {
        // Revert to previous value on error
        setIngredients((prev) =>
          prev.map((i) => {
            if (i.ingredientId !== ingredientId) return i;
            return { ...i, notes: previousValue };
          })
        );
        showToast(
          error instanceof Error ? error.message : 'Failed to update ingredient',
          'error'
        );
      }
    }, UPDATE_DEBOUNCE_MS);

    updateDebounceRef.current.set(ingredientId, timeoutId);
  }

  // Remove ingredient - instant API call
  async function handleRemoveIngredient(ingredientId: number) {
    // Find the ingredient and its index for rollback
    const ingredientIndex = ingredients.findIndex((i) => i.ingredientId === ingredientId);
    const ingredient = ingredients[ingredientIndex];
    if (ingredientIndex === -1 || !ingredient) return;

    // Clear any pending debounced updates for this ingredient
    const existingTimeout = updateDebounceRef.current.get(ingredientId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      updateDebounceRef.current.delete(ingredientId);
    }
    clearQuantitySuccessTimeout(ingredientId);
    setQuantitySaveStates((prev) => {
      const next = { ...prev };
      delete next[ingredientId];
      return next;
    });
    savedQuantityValuesRef.current.delete(ingredientId);

    // Update UI immediately (optimistic update)
    setIngredients((prev) => prev.filter((i) => i.ingredientId !== ingredientId));

    // Skip API call if ingredient was still pending (not yet saved)
    if (ingredient.isPending) {
      return;
    }

    // Call API
    try {
      await removeRecipeIngredient(recipeId, ingredientId);
      invalidateCache(recipeId);
      onCountChange?.(getConfirmedIngredientCount(ingredientsRef.current));
    } catch (error) {
      // Restore ingredient at original position on error
      setIngredients((prev) => {
        const newList = [...prev];
        newList.splice(ingredientIndex, 0, ingredient);
        return newList;
      });
      showToast(
        error instanceof Error ? error.message : 'Failed to remove ingredient',
        'error'
      );
    }
  }

  const noMatchesContent = showQuickCreate ? (
    <div className="p-3 sm:p-4 border-t bg-gray-50 space-y-3">
      <div className="font-medium text-sm">Create New Ingredient</div>

      <div className="space-y-2">
        <Label htmlFor="quick-name" className="text-xs">Name</Label>
        <Input
          id="quick-name"
          type="text"
          value={quickCreateName}
          onChange={(e) => setQuickCreateName(e.target.value)}
          placeholder="Ingredient name"
          disabled={isCreating}
          className="h-9 sm:h-8 text-sm"
        />
      </div>

      {/* Category and Unit - stack on mobile, side by side on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="quick-category" className="text-xs">Category</Label>
          <Select
            value={quickCreateCategory}
            onValueChange={(value) => {
              const nextCategory = value as IngredientCategory;
              // The unit follows the category until the user picks one themselves.
              if (!quickUnitTouched && quickUnitSourceRef.current !== 'suggestion') {
                setQuickCreateUnit(getDefaultUnitForCategory(nextCategory) ?? '');
              }
              setQuickCreateCategory(nextCategory);
            }}
            disabled={isCreating}
          >
            <SelectTrigger id="quick-category" className="h-9 sm:h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INGREDIENT_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {INGREDIENT_CATEGORY_LABELS[cat]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="quick-unit" className="text-xs">Default Unit</Label>
          <Select
            value={quickCreateUnit}
            onValueChange={(value) => {
              markQuickUnitTouched(true);
              setQuickCreateUnit(value);
            }}
            disabled={isCreating}
          >
            <SelectTrigger id="quick-unit" className="h-9 sm:h-8 text-sm">
              <SelectValue placeholder="Select unit" />
            </SelectTrigger>
            <UnitGroupSelectContent />
          </Select>
          {quickUnitNote ? <p className="text-xs text-muted-foreground">{quickUnitNote}</p> : null}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setShowQuickCreate(false);
            resetQuickCreateForm();
          }}
          disabled={isCreating}
          className="flex-1 h-9 sm:h-8"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleQuickCreate}
          disabled={isCreating || !quickCreateName.trim() || !quickCreateUnit}
          className="flex-1 h-9 sm:h-8"
        >
          {isCreating ? 'Creating...' : 'Create & Add'}
        </Button>
      </div>
    </div>
  ) : (
    <>
      <div className="px-4 py-2 text-sm text-gray-500">
        No matching ingredients found
      </div>
      <div className="border-t">
        <button
          type="button"
          onClick={startQuickCreate}
          className="w-full px-4 py-2 text-left hover:bg-green-50 text-green-700 font-medium flex items-center gap-2"
        >
          <span className="text-lg">+</span>
          Create "{latestSearchQuery}"
        </button>
      </div>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-base font-semibold">
          Ingredients ({ingredients.length})
        </Label>

        {/* Scaled preview multiplier (e.g. quantities for 50 thaalis) */}
        <div className="flex items-center gap-2">
          <Label
            htmlFor="thaali-preview-multiplier"
            className="text-sm font-normal text-muted-foreground whitespace-nowrap"
          >
            Preview for
          </Label>
          <Input
            id="thaali-preview-multiplier"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={THAALI_MULTIPLIER_MAX_LENGTH}
            placeholder="e.g. 50"
            value={thaaliMultiplierDraft}
            onChange={(e) => {
              if (!THAALI_MULTIPLIER_DRAFT_PATTERN.test(e.target.value)) return;
              setThaaliMultiplierDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            aria-label="Preview for thaalis"
            className="h-8 w-20 text-sm"
          />
          <span className="text-sm text-muted-foreground">thaalis</span>
        </div>
      </div>

      {/* Search to add ingredients */}
      <SearchableDropdown<Ingredient>
        placeholder="Search ingredients to add... (min 2 chars)"
        onSearch={searchIngredients}
        onSelect={(ingredient) => {
          void handleAddIngredient(ingredient);
        }}
        renderItem={(ing) => (
          <>
            <span>{ing.name}</span>
            <span className="text-xs text-gray-500">{INGREDIENT_CATEGORY_LABELS[ing.category]}</span>
          </>
        )}
        getItemKey={(ing) => ing.id}
        minSearchLength={MIN_SEARCH_LENGTH}
        debounceMs={SEARCH_DEBOUNCE_MS}
        onQueryChange={(query) => {
          setLatestSearchQuery(query);
          setShowQuickCreate(false);
        }}
        noMatchesContent={noMatchesContent}
      />

      {/* Ingredients list */}
      {loadError ? (
        <div className="py-4 text-center border rounded-md bg-red-50">
          <p className="text-red-600">{loadError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadIngredients}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      ) : ingredients.length === 0 ? (
        <div className="py-4 text-center text-gray-500 border rounded-md">
          No ingredients added yet. Search above to add ingredients.
        </div>
      ) : (
        <div className="space-y-3">
          {ingredients.map((ri) => (
            <IngredientRow
              key={ri.ingredientId}
              ingredient={ri}
              thaaliMultiplier={thaaliMultiplier}
              quantitySaveState={quantitySaveStates[ri.ingredientId] ?? { status: 'idle' }}
              onQuantityChange={(value) => handleQuantityChange(ri.ingredientId, value)}
              onQuantityCommit={(value) => void commitQuantity(ri.ingredientId, value)}
              onNotesUpdate={(value) => handleUpdateNotes(ri.ingredientId, value)}
              onRemove={() => handleRemoveIngredient(ri.ingredientId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function getConfirmedIngredientCount(ingredients: LocalIngredient[]): number {
  return ingredients.filter((ingredient) => !ingredient.isPending).length;
}

// Individual ingredient row component
interface IngredientRowProps {
  ingredient: LocalIngredient;
  thaaliMultiplier: number | null;
  quantitySaveState: QuantitySaveState;
  onQuantityChange: (value: string) => void;
  onQuantityCommit: (value: string) => void;
  onNotesUpdate: (value: string) => void;
  onRemove: () => void;
}

function IngredientRow({
  ingredient,
  thaaliMultiplier,
  quantitySaveState,
  onQuantityChange,
  onQuantityCommit,
  onNotesUpdate,
  onRemove,
}: IngredientRowProps) {
  const [showNotes, setShowNotes] = useState(!!ingredient.notes);
  const ingredientName = ingredient.ingredient?.name || 'Unknown';
  const parsedQuantity = parseQuantityPerLarge(ingredient.quantityPerLarge);
  const hasTooLargeQuantity = !parsedQuantity.ok && parsedQuantity.reason === 'tooLarge';
  const hasMissingQuantity = !hasTooLargeQuantity && (!parsedQuantity.ok || parsedQuantity.value === null);
  const hasQuantityError = quantitySaveState.status === 'error';
  const quantityValue = parsedQuantity.ok || parsedQuantity.reason === 'tooLarge' ? parsedQuantity.value : null;
  const scaledQuantity =
    thaaliMultiplier !== null && quantityValue !== null
      ? `${formatScaledQuantity(quantityValue * thaaliMultiplier)} ${ingredient.unit}`
      : null;

  return (
    <div className="border rounded-md p-3 space-y-2">
      {/* Mobile: Stack vertically, Desktop: horizontal layout */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        {/* Ingredient name with remove button on mobile */}
        <div className="flex items-center justify-between sm:flex-1">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium truncate" title={ingredientName}>
                {ingredientName}
                {ingredient.isPending && (
                  <span className="ml-2 text-xs text-blue-600">(saving...)</span>
                )}
              </div>
              {hasMissingQuantity ? (
                <>
                  <Badge variant="outline" className="border-amber-300 text-amber-700">
                    <AlertTriangle className="h-3 w-3" />
                    Qty missing
                  </Badge>
                </>
              ) : null}
              {hasTooLargeQuantity ? (
                <Badge
                  variant="outline"
                  className="border-red-300 text-red-700"
                  title={RECIPE_QUANTITY_PER_SERVING_MAX_MESSAGE}
                >
                  <AlertTriangle className="h-3 w-3" />
                  Qty too large
                </Badge>
              ) : null}
            </div>
          </div>
          {/* Remove button - visible on mobile in header row */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 sm:hidden"
          >
            ×
          </Button>
        </div>

        {/* Quantity and unit row - side by side on mobile too */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
          {/* Quantity for Large */}
          <div className="w-[calc(50%-0.25rem)] sm:w-24 sm:flex-none">
            <div className="relative">
              <Input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]*"
                placeholder="Qty (L)"
                value={ingredient.quantityPerLarge}
                aria-invalid={hasQuantityError}
                aria-label={`Quantity for ${ingredientName}`}
                title={hasQuantityError ? quantitySaveState.message : undefined}
                onChange={(e) => onQuantityChange(e.target.value)}
                onBlur={(e) => onQuantityCommit(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                className={cn(
                  'pr-8 text-sm',
                  hasQuantityError && 'border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500/30'
                )}
              />
              <QuantitySaveIndicator state={quantitySaveState} />
            </div>
          </div>

          {/* Unit (read-only, inherited from ingredient) */}
          <div className="flex w-20 flex-none items-center">
            <span className="text-sm text-muted-foreground px-2">
              {ingredient.unit}
            </span>
          </div>

          {/* Scaled preview quantity (quantityPerLarge × thaali multiplier) */}
          {thaaliMultiplier !== null && (
            <div className="flex w-full items-center gap-1 sm:w-auto sm:flex-none">
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                × {thaaliMultiplier} =
              </span>
              {scaledQuantity ? (
                <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
                  {scaledQuantity}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          )}

          {/* Notes toggle - hidden on mobile, shown on desktop */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowNotes(!showNotes)}
            className="text-gray-500 hidden sm:flex"
          >
            {showNotes ? '−' : '+'} Notes
          </Button>

          {/* Remove button - hidden on mobile, shown on desktop */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 hidden sm:flex"
          >
            ×
          </Button>
        </div>

        {/* Notes toggle button - visible on mobile only */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowNotes(!showNotes)}
          className="text-gray-500 sm:hidden w-full justify-start"
        >
          {showNotes ? '− Hide notes' : '+ Add notes'}
        </Button>
      </div>

      {/* Notes section */}
      {showNotes && (
        <div className="pt-2">
          <Input
            type="text"
            placeholder="Preparation notes..."
            value={ingredient.notes}
            onChange={(e) => onNotesUpdate(e.target.value)}
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
}
